import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useCandidateProfile } from 'candidate_profile_redesign/store';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { withLocalePrefix } from '../../shared/utils/translation';
import {
  Body,
  Label,
  Link,
  SecondaryBody,
  SectionHeader,
} from '@grnhse/seedling/lib/birch/typography';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import RefreshIcon from '@grnhse/seedling/lib/birch/icons/refresh';
import ForwardIcon from '@grnhse/seedling/lib/birch/icons/forward';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { AsyncDropdownField } from 'candidate_profile_redesign/shared/form/Fields';
import LoadingModal from './loading_modal';
import { useFetchModal } from 'candidate_profile_redesign/shared/form/useFetchModal';
import Form, {
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { CompactRow } from '@grnhse/seedling/lib/birch/layouts/compact_row';
import Skeleton from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import { standardRequestWithFlash } from 'shared/utils/request';
import {
  DangerTag,
  SecondaryTag,
} from '@grnhse/seedling/lib/birch/components/tags';
import { WarningCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import CancelButton from '@grnhse/seedling/lib/birch/components/forms/cancel_button';
import SubmitButton from '@grnhse/seedling/lib/birch/components/forms/submit_button';
import type { MergeModalProps } from 'candidate_profile_redesign/store/use_candidate_profile';
import ConditionalTooltip from 'candidate_profile_redesign/shared/conditional_tooltip';
import { useDismissDuplicateAlert } from '../shared/hooks/useAlerts';
import CheckboxField from '@grnhse/seedling/lib/birch/components/forms/fields/checkbox_field';

const t = withLocalePrefix('candidate_profile_redesign.modals.merge');

interface FormData {
  primary_person: Item | null;
  secondary_person: Item | null;
  reject_from_all?: boolean;
}

interface Application {
  hiring_plan_name: string;
  created_at: string;
  source?: string;
  status?: 'rejected' | 'hired' | 'active';
  stage_name?: string;
  rejected_at?: string;
  rejection_reason?: string;
  accepted_at?: string;
  security_concern?: boolean;
}

interface ProspectApplication {
  id: number;
  hiring_plan_names: Array<string>; // we don't use this here but the old front end does
  simple_names: Array<string>;
  offices: Array<string>;
  department: string | null;
}

interface Details {
  full_name: string;
  person_path: string | null;
  title: string | null;
  email_address: string | null;
  phone_number: string | null;
  candidate_applications: Array<Application>;
  prospect_applications: Array<ProspectApplication>;
  reject_all: boolean;
  security_concern: boolean;
}

interface MergeModalResponse {
  potentialPrimaryPerson: Item | null;
  secondaryPerson: Item;
  duplicatePersonOptionsPath: string;
  cardDetailsPath: string;
}

const DetailsContainer = styled.div`
  height: 25vh;
  overflow: auto;
  margin-top: 4px;
  background-color: ${FullPalette.GREY_E25};
  border: 1px solid ${FullPalette.GREY_E500};
  border-radius: 5px;
  padding: 24px;
  width: 100%;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${FullPalette.GREY_E500};
`;

const WarningFooter = styled.div`
  > div {
    border-top: none;
  }
`;

const MiddleColumn = styled(Grid)`
  width: 7%;
  display: flex;
  align-items: center;
  padding: 12px 10px !important;
`;

const ProspectDetails = ({
  applications,
}: {
  applications: Array<ProspectApplication>;
}) => (
  <Stack divider={<Divider />} spacing={2}>
    <SecondaryBody>{t('prospect_for')}</SecondaryBody>
    <Stack divider={<Divider />} spacing={2}>
      {applications.map((a) => {
        const isEmpty =
          !a.simple_names.length && !a.offices.length && !a.department;

        if (isEmpty) {
          return (
            <Body
              medium
              data-provides="prospect-details"
              data-provides-for={a.id}
            >
              {t('prospect_empty')}
            </Body>
          );
        }

        return (
          <Stack
            key={a.id}
            spacing={0}
            data-provides="prospect-details"
            data-provides-for={a.id}
          >
            <Body medium>
              {a.simple_names.length
                ? a.simple_names.join(', ')
                : t('prospect_empty_name')}
            </Body>
            {a.offices.length > 0 && (
              <CompactRow>
                <Label>{t('offices')}</Label>
                <Body>{a.offices.join(', ')}</Body>
              </CompactRow>
            )}
            {a.department && (
              <CompactRow>
                <Label>{t('department')}</Label>
                <Body>{a.department}</Body>
              </CompactRow>
            )}
          </Stack>
        );
      })}
    </Stack>
  </Stack>
);

const ApplicationDetails = ({
  applications,
}: {
  applications: Array<Application>;
}) => (
  <Stack divider={<Divider />} spacing={2}>
    <SecondaryBody>{t('applied_to')}</SecondaryBody>
    {applications.map((a, index) => (
      <Stack
        key={index}
        spacing={0}
        data-provides="application-details"
        data-provides-for={index}
      >
        <CompactRow spacing={1}>
          <Body medium>{a.hiring_plan_name}</Body>
          {a.status === 'rejected' && <DangerTag text={t('rejected')} />}
          {a.status === 'hired' && <SecondaryTag text={t('hired')} />}
        </CompactRow>

        {a.accepted_at && (
          <CompactRow>
            <Label>{t('hired')}</Label>
            <Body>{t('decision_date', { date: a.accepted_at })}</Body>
          </CompactRow>
        )}

        {a.rejected_at && (
          <CompactRow>
            <Label>{t('rejected')}</Label>
            <Body>{t('decision_date', { date: a.rejected_at })}</Body>
          </CompactRow>
        )}

        {a.rejection_reason && (
          <CompactRow>
            <Label>{t('reason')}</Label>
            <RejectionReason securityConcern={a.security_concern}>
              {a.rejection_reason}
            </RejectionReason>
          </CompactRow>
        )}

        {a.stage_name && (
          <CompactRow>
            <Label>{t('stage')}</Label>
            <Body>{a.stage_name}</Body>
          </CompactRow>
        )}
        {a.created_at && (
          <CompactRow>
            <Label>{t('applied')}</Label>
            <Body>{a.created_at}</Body>
          </CompactRow>
        )}
        {a.source && (
          <CompactRow>
            <Label>{t('source')}</Label>
            <Body>{a.source}</Body>
          </CompactRow>
        )}
      </Stack>
    ))}
  </Stack>
);

const NoViewableApplications = () => (
  <Stack divider={<Divider />} spacing={2}>
    <SecondaryBody>{t('applied_to')}</SecondaryBody>
    <Body medium data-provides="no-viewable-applications">
      {t('no_viewable_applications')}
    </Body>
  </Stack>
);

const PersonDetails = ({
  details,
  loading,
}: {
  details: Details | null;
  loading: boolean;
}) => (
  /* @ts-expect-error - undeclared style prop */
  <Grid container style={{ display: 'flex' }}>
    {/* @ts-expect-error - undeclared style prop */}
    <Grid item xs={12} style={{ display: 'flex' }}>
      <DetailsContainer>
        {loading && <Skeleton columnsPerRow={1} rows={4} />}
        {!loading && !details && (
          <Body metadata>{t('no_candidate_selected')}</Body>
        )}
        {details && (
          <Stack>
            <div>
              {details.person_path ? (
                <div>
                  <Link
                    className="pii-blur"
                    target="blank"
                    href={details.person_path}
                    data-amp-mask
                  >
                    {details.full_name}
                  </Link>
                </div>
              ) : (
                <Body className="pii-blur" medium>
                  {details.full_name}
                </Body>
              )}

              {details.title && (
                <Body className="pii-blur">{details.title}</Body>
              )}
              {details.phone_number && (
                <Body className="pii-blur">{details.phone_number}</Body>
              )}
              {details.email_address && (
                <Link
                  className="pii-blur"
                  href={`mailto:${details.email_address}`}
                >
                  {details.email_address}
                </Link>
              )}
            </div>
            {details.candidate_applications.length > 0 && (
              <ApplicationDetails
                applications={details.candidate_applications}
              />
            )}
            {details.prospect_applications.length > 0 && (
              <ProspectDetails applications={details.prospect_applications} />
            )}
            {details.candidate_applications.length === 0 &&
              details.prospect_applications.length === 0 && (
                <NoViewableApplications />
              )}
          </Stack>
        )}
      </DetailsContainer>
    </Grid>
  </Grid>
);

const ModalBody = ({
  optionsPath,
  cardDetailsPath,
  currentPerson,
}: {
  optionsPath?: string;
  cardDetailsPath?: string;
  currentPerson?: Item;
}) => {
  const {
    state: {
      currentUser: {
        permissions: { canMerge },
      },
    },
  } = useCandidateProfile();
  const { watch, setValue } = useFormContext();

  const primaryPerson = watch('primary_person');
  const secondaryPerson = watch('secondary_person');

  const [loadingPrimaryCard, setLoadingPrimaryCard] = useState<boolean>(false);
  const [loadingSecondaryCard, setLoadingSecondaryCard] =
    useState<boolean>(false);
  const [primaryCard, setPrimaryCard] = useState<Details | null>(null);
  const [secondaryCard, setSecondaryCard] = useState<Details | null>(null);

  const getCardDetails = async (personId: string | number) => {
    return standardRequestWithFlash.get(cardDetailsPath, {
      params: { id: personId },
    });
  };

  const showRejectFromAll =
    primaryCard?.reject_all &&
    secondaryCard?.reject_all &&
    (primaryCard?.security_concern || secondaryCard?.security_concern);

  useEffect(() => {
    const loadCardDetails = async () => {
      if (primaryPerson) {
        setLoadingPrimaryCard(true);
        setPrimaryCard(null);
        try {
          const { data } = await getCardDetails(primaryPerson.value);
          setPrimaryCard(data);
          setLoadingPrimaryCard(false);
        } finally {
          setLoadingPrimaryCard(false);
        }
      } else {
        setPrimaryCard(null);
      }

      setValue('reject_from_all', false);
    };

    loadCardDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryPerson]);

  useEffect(() => {
    const loadCardDetails = async () => {
      if (secondaryPerson) {
        setLoadingSecondaryCard(true);
        setSecondaryCard(null);
        try {
          const { data } = await getCardDetails(secondaryPerson.value);
          setSecondaryCard(data);
          setLoadingSecondaryCard(false);
        } finally {
          setLoadingSecondaryCard(false);
        }
      } else {
        setSecondaryCard(null);
      }
    };

    loadCardDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondaryPerson]);

  const swapPeople = () => {
    const tempPerson = primaryPerson;
    setValue('primary_person', secondaryPerson);
    setValue('secondary_person', tempPerson);
  };

  return (
    <DataEntryModal.Body>
      <Stack>
        <Body>{t('body')}</Body>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs>
              <SectionHeader>{t('this_candidate')}</SectionHeader>
            </Grid>
            <MiddleColumn item />
            <Grid item xs>
              <SectionHeader>{t('into_candidate')}</SectionHeader>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs>
              <div className="pii-blur">
                <AsyncDropdownField
                  getOptionsUrl={optionsPath || ''}
                  name="secondary_person"
                  label={t('current_candidate')}
                  placeholder={t('placeholder')}
                  transformValues={(res) => res.results}
                  rules={{ required: true }}
                  disabled={
                    !canMerge || currentPerson?.value === secondaryPerson?.value
                  }
                  preventLoadOnMount
                />
              </div>
            </Grid>
            <MiddleColumn item>
              <IconButton
                label="Swap"
                icon={RefreshIcon}
                onClick={swapPeople}
                data-provides="swap-people"
              />
            </MiddleColumn>
            <Grid item xs>
              <div className="pii-blur">
                <AsyncDropdownField
                  getOptionsUrl={optionsPath || ''}
                  name="primary_person"
                  label={t('other_candidate')}
                  transformValues={(res) => res.results}
                  rules={{ required: true }}
                  placeholder={t('placeholder')}
                  disabled={
                    !canMerge || currentPerson?.value === primaryPerson?.value
                  }
                  preventLoadOnMount
                />
              </div>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item data-provides={'secondary-person-card'} xs>
              <PersonDetails
                loading={loadingSecondaryCard}
                details={secondaryCard}
              />
            </Grid>
            <MiddleColumn item>
              <ForwardIcon size="lg" color={FullPalette.GREEN_600} />
            </MiddleColumn>
            <Grid item data-provides={'primary-person-card'} xs>
              <PersonDetails
                loading={loadingPrimaryCard}
                details={primaryCard}
              />
            </Grid>
          </Grid>
        </Grid>
      </Stack>
      {showRejectFromAll && (
        <CheckboxField
          label={t('reject_from_all_label')}
          value="reject_from_all"
          helperText={t('reject_from_all_helper')}
          name="reject_from_all"
          data-provides="reject_from_all"
        />
      )}
    </DataEntryModal.Body>
  );
};

const Modal = ({
  isOpen,
  closeModal,
  submitting,
  leaveSeparate,
}: {
  isOpen: boolean;
  closeModal: () => void;
  submitting: boolean;
  leaveSeparate: boolean;
}) => {
  const {
    state: {
      currentUser: {
        permissions: { canMerge },
      },
    },
    paths: { mergePersonModalPath },
  } = useCandidateProfile();

  const { data, loading } = useFetchModal<MergeModalResponse>(
    mergePersonModalPath,
    isOpen
  );
  const { mutate: ignoreDuplicateAlert, isPending: isDismissingDuplicate } =
    useDismissDuplicateAlert();
  const { setValue, watch } = useFormContext();

  const primaryPerson = watch('primary_person');
  const secondaryPerson = watch('secondary_person');

  // set initial values
  useEffect(() => {
    if (data) {
      setValue('primary_person', data.potentialPrimaryPerson);
      setValue('secondary_person', data.secondaryPerson);
    }
  }, [data, setValue]);

  if (isOpen && loading) {
    return (
      <LoadingModal submitButtonText={t('merge')} modalTitle={t('header')} />
    );
  }

  const clearDuplicateAlert = () => {
    ignoreDuplicateAlert();
    closeModal();
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      size="large"
      ariaLabel={'merge-modal'}
      onDismiss={closeModal}
      data-provides="merge-modal"
      onSubmit={() => {
        return;
      }}
    >
      <DataEntryModal.Header onClick={closeModal}>
        {t('header')}
      </DataEntryModal.Header>
      <ModalBody
        optionsPath={data?.duplicatePersonOptionsPath}
        cardDetailsPath={data?.cardDetailsPath}
        currentPerson={data?.secondaryPerson}
      />
      <WarningFooter>
        <DataEntryModal.Footer>
          <WarningCallOut
            bodyText={canMerge ? t('callout') : t('cannot_merge_callout')}
          />
        </DataEntryModal.Footer>
      </WarningFooter>
      <DataEntryModal.Footer>
        <CompactRow spacing={2} justifyContent="flex-end">
          {canMerge && leaveSeparate ? (
            <CancelButton
              onClick={clearDuplicateAlert}
              disabled={isDismissingDuplicate}
            >
              {t('leave_separate')}
            </CancelButton>
          ) : (
            <CancelButton onClick={closeModal}>{t('cancel')}</CancelButton>
          )}

          <ConditionalTooltip when={!canMerge} label={t('permission')}>
            <SubmitButton
              disabled={
                !canMerge || !primaryPerson || !secondaryPerson || submitting
              }
              loading={submitting}
            >
              {t('merge')}
            </SubmitButton>
          </ConditionalTooltip>
        </CompactRow>
      </DataEntryModal.Footer>
    </DataEntryModal>
  );
};

const MergeModal = () => {
  const {
    state: {
      header: { jobId },
    },
    actions: { useGlobalModal },
    paths: { mergePersonPath },
  } = useCandidateProfile();

  const { isOpen, closeModal, props } = useGlobalModal('mergeModal');

  const { leaveSeparate, onSuccess } = (props as MergeModalProps) ?? {};

  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (formData: FormData) => {
    if (!formData.secondary_person || !formData.primary_person) {
      return;
    }

    const payload = {
      merge_forward: 'true',
      hiring_plan_id: jobId,
      person_a: formData.secondary_person.value,
      person_b: formData.primary_person.value,
      reject_from_all: formData.reject_from_all,
    };

    try {
      setSubmitting(true);
      const { data } = await standardRequestWithFlash.post(
        mergePersonPath,
        payload
      );
      if (onSuccess) {
        onSuccess();
      } else if (data.goto) {
        window.location.href = data.goto;
      }
    } catch (e) {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      <Modal
        isOpen={isOpen}
        closeModal={closeModal}
        submitting={submitting}
        data-provides={'merge-person-modal'}
        leaveSeparate={!!leaveSeparate}
      />
    </Form>
  );
};

const RejectionReason = styled(Body)<{ securityConcern?: boolean }>`
  color: ${(props) =>
    props.securityConcern ? FullPalette.POPPY_600 : undefined};
`;

export default MergeModal;
